import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { isArray, isEmpty, isEqual, isNil, noop } from 'lodash';
// Import Components
import MenuItemDropdown from 'components/MenuItemDropdown';
// Import constants
import { l } from 'constants/common';
import { MenuItemDropdownRef } from 'components/MenuItemDropdown/types';

interface Props {
    value?: string | string[];
    menuItemId: number;
    onChange: (selectedValues: (string | string[])[]) => void;
    displayName: string;
    placeholder?: string;
    isMultiselect?: boolean;
}

interface SimpleMenuItemDropdownFormRef {
    submitForm: () => Promise<boolean>;
    reset: () => void;
}

const SimpleMenuItemDropdownForm = forwardRef<SimpleMenuItemDropdownFormRef, Props>((props, ref) => {
    const { value = [], menuItemId, onChange, displayName, placeholder, isMultiselect } = props;
    const { t } = useTranslation();
    const menuItemRef = useRef<MenuItemDropdownRef>(null);
    const previousValueRef = useRef<string | string[]>();
    const [isLoading, setIsLoading] = useState();
    const formik = useFormik({
        initialValues: {
            menuItemField: '',
        },
        onSubmit: noop,
        validationSchema: Yup.object({
            menuItemField: Yup.array().min(1, t(l.CannotBeEmpty, { fieldName: t(l.Name) })),
        }),
    });

    const { errors, isValid, setFieldValue, submitForm, validateForm, setFieldTouched, values } = formik;

    useImperativeHandle(ref, () => ({
        submitForm() {
            return new Promise((resolve, reject) => {
                submitForm()
                    .then(() =>
                        validateForm(values).then((formErrors) => {
                            resolve(isEmpty(formErrors));
                            return;
                        }),
                    )
                    .catch(() => reject());
            });
        },
        reset() {
            setFieldTouched('menuItemField', false);
        },
    }));

    const onIsLoadingChange = useCallback((isLoading) => {
        setIsLoading(isLoading);
    }, []);

    const onChangeValue = (e) => {
        if (isLoading !== false) return;
        setFieldValue('menuItemField', e?.value ?? []);
        setFieldValue('menuItemField', isNil(e?.value) ? [] : isArray(e?.value) ? e?.value : [e?.value]);
        onChange(e);
    };

    useEffect(() => {
        if (!isEqual(value, previousValueRef.current) && isLoading === false) {
            setFieldValue('menuItemField', value);
            menuItemRef.current?.updateValue?.(value);
            previousValueRef.current = value;
        }
    }, [setFieldValue, value, isLoading]);

    return (
        <MenuItemDropdown
            ref={menuItemRef}
            defaultValue={{ value }}
            menuItemId={menuItemId}
            getUpdate={onChangeValue}
            onIsLoadingChange={onIsLoadingChange}
            displayName={displayName}
            placeholder={placeholder}
            isMultiselect={isMultiselect}
            isValid={isValid}
            errorText={errors.menuItemField}
        />
    );
});

SimpleMenuItemDropdownForm.displayName = 'SimpleMenuItemDropdownForm';

export default memo(SimpleMenuItemDropdownForm);
